import { useEffect, useRef } from "react";

/**
 * Idle-timeout auto-logout hook.
 *
 * - Starts a `timeoutMs` timer when `enabled` is true.
 * - Any of these user-interaction events resets the timer:
 *     mousedown, keydown, touchstart, scroll, wheel.
 * - The last-activity timestamp is mirrored to localStorage so that activity
 *   in another tab also keeps this tab logged in (cross-tab sync).
 * - When the tab is hidden and timers get throttled, the timer is re-anchored
 *   to the stored last-activity timestamp on `visibilitychange`.
 * - When the timer expires, `onTimeout` is invoked. The caller decides what
 *   that means (clear cookie, redirect, etc.).
 */
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
];

const STORAGE_KEY = "lx_last_activity_ts";

/** Reset idle timer baseline (call after successful login). */
export const touchIdleActivity = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (_) {
    // localStorage may be unavailable; ignore.
  }
};

const readActivityTimestamp = (timeoutMs) => {
  const now = Date.now();
  if (typeof window === "undefined") return now;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed) && now - parsed < timeoutMs) {
      return parsed;
    }
    window.localStorage.setItem(STORAGE_KEY, String(now));
  } catch (_) {
    // ignore
  }
  return now;
};

export const useIdleLogout = ({ enabled, timeoutMs, onTimeout }) => {
  const timerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!enabled || !timeoutMs || typeof window === "undefined") return undefined;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const fire = () => {
      try {
        onTimeoutRef.current && onTimeoutRef.current();
      } catch (err) {
        console.error("Idle logout handler failed:", err);
      }
    };

    const scheduleFrom = (lastTs) => {
      clearTimer();
      const elapsed = Date.now() - lastTs;
      const remaining = Math.max(0, timeoutMs - elapsed);
      if (remaining === 0) {
        fire();
        return;
      }
      timerRef.current = setTimeout(fire, remaining);
    };

    const onActivity = () => {
      const now = Date.now();
      try {
        window.localStorage.setItem(STORAGE_KEY, String(now));
      } catch (_) {
        // ignore
      }
      scheduleFrom(now);
    };

    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      const ts = parseInt(e.newValue, 10);
      if (!Number.isFinite(ts)) return;
      const elapsed = Date.now() - ts;
      if (elapsed >= timeoutMs) {
        touchIdleActivity();
        scheduleFrom(Date.now());
        return;
      }
      scheduleFrom(ts);
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      scheduleFrom(readActivityTimestamp(timeoutMs));
    };

    scheduleFrom(readActivityTimestamp(timeoutMs));

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true }),
    );
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity),
      );
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, timeoutMs]);
};

export default useIdleLogout;
